import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { Copy, History, RefreshCw, Trash2, UserPlus } from 'lucide-react';
import { useState, type ComponentType, type ReactNode } from 'react';
import type { FieldValues } from 'react-hook-form';
import type { z } from 'zod';

import { defineAction, useActionDialogs, type ActionBarItem, type ActionBinding } from '@/shared/action';
import type { ApiError } from '@/shared/http';
import { useIsMobile } from '@/shared/mobile';
import { BlockSkeleton, EmptyState, ErrorState } from '@/shared/page';
import { resolvePatternText, type PatternText } from '@/shared/patterns';
import { Drawer, DrawerBody, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/shared/ui/drawer';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/shared/ui/sheet';

import { Timeline, type TimelineItem } from './timeline';

/** O registro aberto: id do fio e o nome que vai no diálogo e no toast. */
export interface RecordRef {
  readonly id: string;
  readonly name: string;
}

/** Leitura da auditoria já no formato da linha do tempo (o mapper do módulo converte). */
export type HistoryQuery = UseQueryOptions<readonly TimelineItem[], ApiError, readonly TimelineItem[]>;

/** Ação de registro com formulário curto (clonar escolhe o correspondente; atribuir, operador ou grupo). */
export interface RecordFormAction<V extends FieldValues, R> {
  readonly Fields: ComponentType<{ readonly ctx: RecordRef }>;
  readonly schema: (ctx: RecordRef, i18n: I18n) => z.ZodType<V, V>;
  readonly defaults: (ctx: RecordRef) => V;
  /** Mutação do módulo sem feedback próprio (X06). */
  readonly submit: (values: V, ctx: RecordRef) => Promise<R>;
  /** Toast de sucesso da tela (X06): troca o padrão pelo texto do legado. */
  readonly success?: (ctx: RecordRef) => { readonly title: PatternText; readonly description?: PatternText };
}

export interface UseRecordActionsOptions<CV extends FieldValues, CR, AV extends FieldValues, AR> {
  /** Sem registro (carregando, cadastro novo): nenhuma ação. */
  readonly record: RecordRef | undefined;
  /** "Atualizar": relê o registro (refetch), sem diálogo. */
  readonly refresh?: () => void;
  /** Clonar: cópia do registro; `open` leva para a cópia (o legado navega para o id novo). */
  readonly clone?: RecordFormAction<CV, CR> & { readonly open?: (result: CR) => void };
  readonly assign?: RecordFormAction<AV, AR>;
  /** Histórico de ajustes (auditoria da SearchApi): a query só roda com o painel aberto. */
  readonly history?: {
    readonly query: HistoryQuery;
    readonly description?: PatternText;
    /** Conteúdo próprio do painel (o bloco de histórico do módulo, U41) no lugar da linha do tempo da `query`. */
    readonly content?: ReactNode;
  };
  /** Excluir: confirmação com o verbo e o nome (U20-6); `after` sai da tela (volta para a lista). */
  readonly remove?: {
    readonly submit: (ctx: RecordRef) => Promise<unknown>;
    readonly after?: () => void;
    readonly summary?: PatternText;
    readonly success?: (ctx: RecordRef) => { readonly title: PatternText; readonly description?: PatternText };
  };
}

/** Ids fixos das ações de registro, para casar com a tabela status × ação da tela. */
export type RecordActionId = 'refresh' | 'clone' | 'assign' | 'history' | 'remove';

export interface RecordActions {
  /** Na ordem do protótipo: Atualizar, Clonar, Atribuir, Histórico, Excluir. */
  readonly items: readonly ActionBarItem[];
  /** As mesmas, como `ActionBinding`, para `actionBarItems(tabela, { ...bindings, ...daTela })`. */
  readonly bindings: Readonly<Partial<Record<RecordActionId, ActionBinding & { readonly kind: ActionBarItem['kind'] }>>>;
  /** Ponha uma vez na tela: diálogos e o painel de histórico. */
  readonly element: ReactNode;
}

function HistoryPanel({
  query,
  description,
  content,
  open,
  onOpenChange,
}: {
  readonly query: HistoryQuery;
  readonly description?: string | undefined;
  readonly content?: ReactNode;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const { t } = useLingui();
  const isMobile = useIsMobile();
  const result = useQuery<readonly TimelineItem[], ApiError, readonly TimelineItem[]>({ ...query, enabled: open && content === undefined });
  const body = content !== undefined ? (
    open ? content : null
  ) : result.isPending ? (
    <BlockSkeleton kind="list" rows={3} />
  ) : result.isError ? (
    <ErrorState error={result.error} onRetry={() => void result.refetch()} retrying={result.isFetching} compact />
  ) : result.data.length === 0 ? (
    <EmptyState compact icon={History} title={t`Nenhum ajuste registrado`} />
  ) : (
    <Timeline items={result.data} compact={isMobile} label={t`Ajustes do registro`} />
  );
  const title = t`Histórico de ajustes`;
  const subtitle = description ?? t`Acompanhe os registros de alteração`;
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="h-[92dvh]">
          <DrawerHeader>
            <DrawerTitle>{title}</DrawerTitle>
            <DrawerDescription>{subtitle}</DrawerDescription>
          </DrawerHeader>
          <DrawerBody>{body}</DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent size="sm">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>{subtitle}</SheetDescription>
        </SheetHeader>
        <SheetBody>{body}</SheetBody>
      </SheetContent>
    </Sheet>
  );
}

/**
 * As ações de registro que o legado repetia em 20 telas (P08): atualizar, clonar, atribuir,
 * histórico e excluir, com o contrato da U39 §6 (diálogo com verbo, toast da tela, X06). A tela
 * passa só o que o registro tem; o resto não aparece.
 */
export function useRecordActions<CV extends FieldValues = FieldValues, CR = unknown, AV extends FieldValues = FieldValues, AR = unknown>(
  options: UseRecordActionsOptions<CV, CR, AV, AR>,
): RecordActions {
  const { i18n, t } = useLingui();
  const dialogs = useActionDialogs();
  const [historyOpen, setHistoryOpen] = useState(false);
  const { record } = options;
  const bindings: Partial<Record<RecordActionId, ActionBinding & { readonly kind: ActionBarItem['kind'] }>> = {};

  if (record && options.refresh) {
    const refresh = options.refresh;
    bindings.refresh = { kind: 'read', label: msg`Atualizar`, icon: RefreshCw, iconOnly: true, onSelect: refresh };
  }
  if (record && options.clone) {
    const clone = options.clone;
    const action = defineAction<RecordRef, CV, CR>({
      id: 'record.clone',
      kind: 'write',
      icon: Copy,
      title: (ctx) => t`Clonar ${ctx.name}`,
      summary: () => msg`Faz uma cópia do registro atual no correspondente escolhido. Você é levado para a cópia em seguida.`,
      record: (ctx) => ctx.name,
      verb: msg`clonar`,
      Fields: clone.Fields,
      schema: clone.schema,
      defaults: clone.defaults,
      submit: clone.submit,
      success: (_result, _values, ctx) => clone.success?.(ctx) ?? { title: msg`Registro clonado`, description: t`Cópia de ${ctx.name}` },
    });
    bindings.clone = {
      kind: 'write',
      label: msg`Clonar registro`,
      icon: Copy,
      onSelect: () => dialogs.open(action, record, (results) => {
        const created = results[0];
        if (created !== undefined) clone.open?.(created);
      }),
    };
  }
  if (record && options.assign) {
    const assign = options.assign;
    const action = defineAction<RecordRef, AV, AR>({
      id: 'record.assign',
      kind: 'write',
      icon: UserPlus,
      title: () => msg`Atribuir`,
      summary: (ctx) => t`Selecione um correspondente, operador ou grupo para ${ctx.name}.`,
      record: (ctx) => ctx.name,
      verb: msg`atribuir`,
      Fields: assign.Fields,
      schema: assign.schema,
      defaults: assign.defaults,
      submit: assign.submit,
      success: (_result, _values, ctx) => assign.success?.(ctx) ?? { title: msg`Registro atribuído`, description: ctx.name },
    });
    bindings.assign = { kind: 'write', label: msg`Atribuir`, icon: UserPlus, onSelect: () => dialogs.open(action, record) };
  }
  if (record && options.history) {
    bindings.history = { kind: 'read', label: msg`Histórico de ajustes`, icon: History, onSelect: () => setHistoryOpen(true) };
  }
  if (record && options.remove) {
    const remove = options.remove;
    const action = defineAction<RecordRef>({
      id: 'record.remove',
      kind: 'destructive',
      icon: Trash2,
      title: (ctx) => t`Excluir ${ctx.name}?`,
      summary: () => remove.summary ?? msg`O registro sai da lista e não pode ser recuperado.`,
      record: (ctx) => ctx.name,
      verb: msg`excluir`,
      submit: (_values, ctx) => remove.submit(ctx),
      success: (_result, _values, ctx) => remove.success?.(ctx) ?? { title: msg`Registro excluído`, description: ctx.name },
    });
    bindings.remove = { kind: 'destructive', label: msg`Excluir`, icon: Trash2, onSelect: () => dialogs.open(action, record, () => remove.after?.()) };
  }

  const order: readonly RecordActionId[] = ['refresh', 'clone', 'assign', 'history', 'remove'];
  const items = order.flatMap((id): ActionBarItem[] => {
    const binding = bindings[id];
    if (!binding) return [];
    const { disabledReason: _reason, ...rest } = binding;
    return [{ ...rest, id }];
  });
  const history = options.history;
  const description = history?.description === undefined ? undefined : resolvePatternText(i18n, history.description);

  return {
    items,
    bindings,
    element: (
      <>
        {dialogs.element}
        {history ? <HistoryPanel query={history.query} description={description} content={history.content} open={historyOpen} onOpenChange={setHistoryOpen} /> : null}
      </>
    ),
  };
}
